import {
  VERTICAL_POSITIONS,
  HORIZONTAL_POSITIONS,
} from "src/helpers/constants";
import {
  Wall,
  Configuration,
  Position,
  Coordinate,
} from "src/types/configuration";
import ConfigurationService from "src/services/configurationService";
import ConfiguratorService from "src/services/configuratorService";

export const getWallPosition = (wallId: string): Position => {
  if (!wallId) return defaultPosition();

  // @ts-ignore
  return (
    // @ts-ignore
    HORIZONTAL_POSITIONS[wallId] ||
    // @ts-ignore
    VERTICAL_POSITIONS[wallId] ||
    defaultPosition()
  );
};

const defaultPosition = (): Position => ({
  start: { x: 0, y: 0 },
  end: { x: 0, y: 0 },
});

/**
 * Checks if object by coordinates is vertical
 *
 * @param {Coordinate} start - start coordiante
 * @param {Coordinate} end - end coordiante
 *
 * @returns {boolean} - is vertical
 */
export const isVertical = (start: Coordinate, end: Coordinate): boolean => {
  return start.x === end.x;
};

export const findWall = (
  wallId: string,
  walls: Wall[],
  configuration: ConfigurationService,
  inner: boolean = false
): Wall => {
  const foundWall = walls.find((wall) => wall.id === wallId);

  return foundWall
    ? { ...foundWall }
    : configuration.getDefaultWall(wallId, inner);
};

/**
 * Adds walls to a house based on the specified side and configuration.
 *
 * @param {'left' | 'right'} side - The side of the house where the walls should be added ('left' or 'right').
 * @param {ConfigurationService} configuration - The service used for fetching default wall configurations.
 * @param {ConfiguratorService} configurator
 * @param walls
 * @return {Wall[]} - An updated array of walls after adding the specified house walls.
 */
export function addHouseWalls(
  side: "left" | "right",
  configuration: ConfigurationService,
  configurator: ConfiguratorService,
  walls: Wall[]
): Wall[] {
  if (side === "left") {
    const wallIds = ["h-0-0", "h-1-0", "v-0-0", "v-0-1"];

    const houseWalls: Wall[] = wallIds.map((wallId) => {
      let wall = findWall(wallId, walls, configuration, false);

      if (wall.storage === "empty" && wall.id === "h-0-0") {
        wall.storage = "left_front";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "dichte_73cm";
        wall.woodWall.doorSide = "right";
        wall.woodWall.window = "raam";
        wall.slidingGlassWall = "empty";
      } else if (wall.storage === "empty" && wall.id === "h-1-0") {
        wall.storage = "left_back";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        wall.woodWall.window = "empty";
        wall.slidingGlassWall = "empty";
      } else if (wall.storage === "empty" && wall.id === "v-0-1") {
        wall.inner = true;
        wall.storage = "left_right";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        wall.woodWall.window = "empty";
        wall.woodWall.rotate = true;
        wall.slidingGlassWall = "empty";
      } else if (wall.storage === "empty" && wall.id === "v-0-0") {
        wall.storage = "left_left";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        // wall.woodWall.window = "raam";
        wall.woodWall.window = "empty";
        wall.slidingGlassWall = "empty";
      } else if (wall.storage === "right_left" && wall.id === "v-0-1") {
        wall = configuration.getDefaultWall(wallId, false);
        wall.inner = true;
        wall.storage = "left_right";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        wall.woodWall.window = "empty";
        wall.woodWall.rotate = true;
        wall.slidingGlassWall = "empty";
      }

      return wall;
    });

    const updatedWalls = walls.filter(
      (wall) => !wallIds.includes(wall.id) || wall.storage === "right_left"
    );
    return [...updatedWalls, ...houseWalls];
  }

  if (side === "right") {
    const columns = configurator.getColumns();
    const wallIds = [
      `h-0-${columns - 1}`,
      `h-1-${columns - 1}`,
      `v-0-${columns - 1}`,
      `v-0-${columns}`,
    ];

    const houseWalls: Wall[] = wallIds.map((wallId) => {
      let wall = findWall(wallId, walls, configuration, false);

      if (wall.storage === "empty" && wall.id === `h-0-${columns - 1}`) {
        wall.storage = "right_front";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "dichte_73cm";
        wall.woodWall.doorSide = "right";
        wall.woodWall.side = "right";
        wall.woodWall.window = "raam";
        wall.slidingGlassWall = "empty";
      } else if (wall.storage === "empty" && wall.id === `h-1-${columns - 1}`) {
        wall.storage = "right_back";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        wall.woodWall.window = "empty";
        wall.slidingGlassWall = "empty";
      } else if (wall.storage === "empty" && wall.id === `v-0-${columns - 1}`) {
        wall.inner = true;
        wall.storage = "right_left";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        wall.woodWall.window = "empty";
        wall.slidingGlassWall = "empty";
      } else if (wall.storage === "empty" && wall.id === `v-0-${columns}`) {
        wall.storage = "right_right";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        // wall.woodWall.window = "raam";
        wall.woodWall.window = "empty";
        wall.slidingGlassWall = "empty";
        wall.isRightSide = true;
      } else if (
        wall.storage === "left_right" &&
        wall.id === `v-0-${columns - 1}`
      ) {
        wall = configuration.getDefaultWall(wallId, false);
        wall.inner = true;
        wall.storage = "right_left";
        wall.woodWall.outerColor = "wall_vuren_black";
        wall.woodWall.door = "empty";
        wall.woodWall.doorSide = "left";
        wall.woodWall.window = "empty";
        wall.slidingGlassWall = "empty";
      }

      return wall;
    });

    const updatedWalls = walls.filter(
      (wall) => !wallIds.includes(wall.id) || wall.storage === "left_right"
    );
    return [...updatedWalls, ...houseWalls];
  }

  return walls;
}

/**
 * Removes specific walls from the house based on the given side and configuration.
 *
 * @param {'left' | 'right'} side - Specifies which side of the house the walls should be removed from (either 'left' or 'right').
 * @param walls
 * @return {Wall[]} - A filtered array of walls after removing the specified walls from the given side.
 */
export function removeHouseWalls(
  side: "left" | "right",
  walls: Wall[]
): Wall[] {
  if (side === "left") {
    return walls.filter((wall) => !wall.storage.startsWith("left"));
  }

  if (side === "right") {
    return walls.filter((wall) => !wall.storage.startsWith("right"));
  }

  return walls;
}
